import { useCallback, useEffect, useMemo, useState } from "react";

import { Alert, Button, Stack, Typography } from "@mui/material";
import { PageContainer } from "@/features/ui";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  DEFAULT_VALUES,
  getLineCostPerUnit,
  MAX_MANUAL_EDIT_LINES,
  StockAdjustmentForm,
  StockAdjustmentTypeEnum,
} from "@/features/stock-adjustments";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditStockAdjustment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stockAdjustment, setStockAdjustment] = useState(null);
  const [error, setError] = useState(null);

  const loadStockAdjustment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`stock-adjustments/${referenceNumber}`, {
        params: {
          withOutlet: true,
          withWarehouse: true,
          withAdjustmentChartOfAccount: true,
        },
      });
      setStockAdjustment(data);
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat penyesuaian stok"));
    } finally {
      setLoading(false);
    }
  }, [referenceNumber]);

  useEffect(() => {
    loadStockAdjustment();
  }, [loadStockAdjustment]);

  const defaultValues = useMemo(() => {
    if (!stockAdjustment) return DEFAULT_VALUES;

    return {
      outlet: stockAdjustment.outlet,
      warehouse: stockAdjustment.warehouse,
      adjustmentChartOfAccount:
        stockAdjustment.adjustmentOutletChartOfAccount || null,
      transactionDate: dayjs(stockAdjustment.transactionDate),
      note: stockAdjustment.note || "",
      stockAdjustmentProductVariants: (
        stockAdjustment.stockAdjustmentProductVariants || []
      ).map((line) => ({
        id: line.id,
        name: line.name,
        sku: line.sku,
        unitOfMeasurement: line.product?.unitOfMeasurement?.name ?? "",
        type:
          line.quantity < 0
            ? StockAdjustmentTypeEnum.DEDUCTION
            : StockAdjustmentTypeEnum.ADDITION,
        quantity: Math.abs(line.quantity),
        // The line's own batch (its positive link), not whichever link comes first: a consumed
        // stock-in also carries negative links, and sending their cost back would re-cost it.
        costPerUnit: getLineCostPerUnit(line),
        note: line.note || "",
      })),
    };
  }, [stockAdjustment]);

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const { outletId, ...payload } = data;

      await apiOutletClient.patch(
        `stock-adjustments/${referenceNumber}`,
        payload,
        {
          headers: { "Outlet-Id": outletId },
        },
      );

      showSnackbar("Penyesuaian stok berhasil diperbarui", "success");
      navigate(`/inventory/stock-adjustments/${referenceNumber}`);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memperbarui penyesuaian stok"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const lineCount = stockAdjustment?.stockAdjustmentProductVariants?.length ?? 0;
  if (lineCount > MAX_MANUAL_EDIT_LINES) {
    // The form keeps one editable row per line; thousands of them freeze the page. The page is
    // reachable by URL, so the guard lives here as well as on the detail page's button.
    return (
      <PageContainer
        maxWidth="xl"
        title="Edit Penyesuaian Stok"
        breadcrumbs={[
          { title: "Penyesuaian Stok", path: "/inventory/stock-adjustments" },
          {
            title: referenceNumber,
            path: `/inventory/stock-adjustments/${referenceNumber}`,
          },
          { title: "Edit" },
        ]}
      >
        <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
          <Alert severity="info">
            Penyesuaian stok ini berisi {lineCount.toLocaleString("id-ID")}{" "}
            baris, terlalu banyak untuk diubah manual (maksimal{" "}
            {MAX_MANUAL_EDIT_LINES} baris). Untuk mengubah harga, gunakan
            &quot;Update Harga dari File&quot; di halaman detail.
          </Alert>
          <Button
            variant="contained"
            onClick={() =>
              navigate(`/inventory/stock-adjustments/${referenceNumber}`)
            }
          >
            Kembali ke Detail
          </Button>
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Edit Penyesuaian Stok"
      breadcrumbs={[
        {
          title: "Penyesuaian Stok",
          path: "/inventory/stock-adjustments",
        },
        {
          title: referenceNumber,
          path: `/inventory/stock-adjustments/${referenceNumber}`,
        },
        { title: "Edit" },
      ]}
    >
      <StockAdjustmentForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        editMode
        renderActionButton={() => (
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            size="large"
          >
            {isSubmitting ? "Menyimpan..." : "Simpan"}
          </Button>
        )}
      />
    </PageContainer>
  );
};
